import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Heart, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { createGoodDeedEntry, getGoodDeedsEntries, getLocalDateString, parseLocalDateKey } from '../lib/api';

export default function GoodDeeds({ user }) {
  const { t, i18n } = useTranslation();
  const isHr = i18n.language === 'hr';
  const [deedsList, setDeedsList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: getLocalDateString(),
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchDeeds();
  }, []);

  const fetchDeeds = async () => {
    setErrorMessage('');
    try {
      const data = await getGoodDeedsEntries();
      setDeedsList(data);
    } catch (error) {
      console.error('Error fetching good deeds:', error);
      setDeedsList([]);
      setErrorMessage(isHr ? 'Učitavanje dobrih djela nije uspjelo.' : 'Failed to load good deeds.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      await createGoodDeedEntry({
        title: formData.title,
        description: formData.description,
        date: formData.date,
      });
      setShowModal(false);
      setFormData({
        title: '',
        description: '',
        date: getLocalDateString(),
      });
      toast.success(isHr ? 'Dobro djelo je spremljeno.' : 'Good deed saved.');
      fetchDeeds();
    } catch (error) {
      console.error('Error creating good deed:', error);
      const message = isHr ? 'Spremanje dobrog djela nije uspjelo.' : 'Failed to save good deed.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#292524] mb-2">
              {t('good_deeds_log')}
            </h1>
            <p className="text-[#57534E] font-body">
              Track your acts of kindness and good deeds
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-good-deed-button"
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all active:scale-95 shadow-md"
          >
            <Plus className="w-5 h-5" />
            {t('add')}
          </button>
        </div>

        <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
              <Heart className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-[#57534E]">{t('total')} Good Deeds</p>
              <p className="text-3xl font-heading font-bold text-primary" data-testid="total-deeds-count">
                {deedsList.length}
              </p>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-testid="good-deeds-error-message"
          >
            {errorMessage}
          </div>
        )}

        <div className="space-y-3">
          {deedsList.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-12 text-center" data-testid="good-deeds-empty-state">
              <Heart className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] font-body">
                No good deeds recorded yet
              </p>
              <p className="text-sm text-[#A8A29E] mt-2">
                Start logging your acts of kindness
              </p>
            </div>
          ) : (
            deedsList.map((deed) => (
              <div
                key={deed.id}
                className="bg-white border border-stone-200/60 rounded-xl p-5 shadow-sm hover:shadow-md transition-all"
                data-testid={`deed-item-${deed.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Heart className="w-5 h-5 text-accent" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-[#292524] mb-1">
                      {deed.title}
                    </h3>
                    <p className="text-[#57534E] font-body mb-2">
                      {deed.description}
                    </p>
                    <p className="text-sm text-[#A8A29E]">
                      {parseLocalDateKey(deed.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-semibold text-[#292524]">
                {t('add_good_deed')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                data-testid="close-deed-modal"
                className="p-2 hover:bg-stone-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {t('title')}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  data-testid="deed-title-input"
                  placeholder="e.g., Helped a neighbor"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {t('description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  data-testid="deed-description-input"
                  placeholder="Describe what you did..."
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  rows="4"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#292524] mb-2">
                  {t('date')}
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  data-testid="deed-date-input"
                  className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="deed-submit-button"
                className="w-full py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('loading') : t('save')}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}